import { useCallback } from 'react'
import type { NeedsAnalysisState } from './NeedsAnalysisContext'
import { SCREEN_TYPES, type ScreenType } from './useScreenSequence'

/**
 * Hook to validate the current step and overall form
 */
export function useFormValidation(state: NeedsAnalysisState, screens: readonly ScreenType[]) {
  const canProceedFromScreen = useCallback(
    (screen: ScreenType | undefined): boolean => {
      switch (screen) {
        case SCREEN_TYPES.INTRO:
          return true
        case SCREEN_TYPES.LANGUAGE: // Q0 (optional)
          return true
        case SCREEN_TYPES.ENGLISH: // Q2 (required when shown)
          return !!state.self_assessed_english_level
        case SCREEN_TYPES.TIME: // Q3 (required)
          return !!state.weekly_time_commitment
        case SCREEN_TYPES.GOALS: // Q4 (required, at least 1)
          return state.primary_goals.length > 0
        case SCREEN_TYPES.INDUSTRIES: // Q5 (required if shown)
          return state.industry_interests.length > 0
        case SCREEN_TYPES.TEST_TYPE: // Q6 (required if shown)
          return !!state.goal_specification
        case SCREEN_TYPES.COMMUNICATION_CHANNELS: // Q7 (required, at least 1)
          return state.communication_channels.length > 0
        case SCREEN_TYPES.COMMUNICATION_FREQUENCY: // Q8 (required)
          return !!state.communication_frequency
        default:
          return false
      }
    },
    [state]
  )

  // The form is valid when every screen the learner is actually shown is answered.
  const isFormValid = useCallback(
    (): boolean => screens.every((screen) => screen === SCREEN_TYPES.DONE || canProceedFromScreen(screen)),
    [screens, canProceedFromScreen]
  )

  const canProceedFromCurrentStep = useCallback((): boolean => {
    const currentScreen = screens[state.currentStep]
    return currentScreen === SCREEN_TYPES.DONE ? isFormValid() : canProceedFromScreen(currentScreen)
  }, [screens, state.currentStep, canProceedFromScreen, isFormValid])

  return { canProceedFromCurrentStep, isFormValid }
}
